import React, { Dispatch, FC, SetStateAction, useMemo } from "react";

import { TechSheetFieldType } from "../../../../types/products";
import {
  getDefaultProductFinderFilters,
  getTechSheetFieldsListFromSpecializations,
} from "../../../../utils/products";
import { slugify } from "../../../../utils/slugify";
import { Chip } from "../../../forms";
import { TagColors, TagDimensions } from "../../../forms/elements/tags/types";
import { materialsOptions } from "../../../forms/forms-fields/materials-form-fields/constants";
import {
  MaterialOption,
  MaterialsFormFieldsLabels,
} from "../../../forms/forms-fields/materials-form-fields/types";
import { EMPTY_OPTION_VALUE } from "../constants";
import { ProductFinderFiltersFormTypes } from "../types";

export interface ProductFiltersSummaryProps {
  appliedFilters: ProductFinderFiltersFormTypes;
  setAppliedFilters: Dispatch<SetStateAction<ProductFinderFiltersFormTypes>>;
  specializations: Queries.MachineSpecializationDataFragment[];
  labels: MaterialsFormFieldsLabels & {
    "product-finder-panel-machine-type": string;
    "product-finder-panel-clear-filters": string;
  };
}

export const ProductFiltersSummary: FC<ProductFiltersSummaryProps> = ({
  labels,
  specializations,
  appliedFilters,
  setAppliedFilters,
}) => {
  const isSingleSpecialization = specializations.length === 1;
  const { machiningTypeId, materials, techSheetFieldsSlugs } = appliedFilters;

  const machiningType = specializations
    .map((specialization) => specialization.machiningType)
    .find((mt) => mt?.id === machiningTypeId);

  const allTechSheetFields = useMemo(() => {
    return getTechSheetFieldsListFromSpecializations(specializations).filter((field) => {
      if (!field?.internalName) return false;
      const value = techSheetFieldsSlugs[slugify(field.internalName)];
      if (field.valueType === TechSheetFieldType.MULTI_SELECT) {
        return Array.isArray(value) && value.filter(Boolean).length > 0;
      } else {
        return value;
      }
    });
  }, [specializations, techSheetFieldsSlugs]);

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        {!isSingleSpecialization &&
          materialsOptions
            .filter((key) => materials[key])
            .map((key) => (
              <Chip
                checked
                readOnly
                dimension={TagDimensions.Medium}
                onChange={() => {
                  setAppliedFilters((filters) => ({
                    ...filters,
                    materials: {
                      ...filters.materials,
                      [key as MaterialOption]: false,
                    },
                  }));
                }}
                key={key}
                color={TagColors.Neutral}
              >
                {labels?.[key as keyof MaterialsFormFieldsLabels]}
              </Chip>
            ))}

        {!isSingleSpecialization && machiningType && (
          <Chip
            checked
            readOnly
            color={TagColors.Neutral}
            onChange={() => {
              setAppliedFilters((filters) => ({
                ...filters,
                machiningTypeId: EMPTY_OPTION_VALUE,
              }));
            }}
          >
            {machiningType.name}
          </Chip>
        )}

        {allTechSheetFields.map((field) => {
          if (!field) return null;
          const values = field.internalName
            ? techSheetFieldsSlugs[slugify(field.internalName)]
            : null;

          const renderedValues =
            field.valueType === TechSheetFieldType.TEXT
              ? [values]
              : (
                  (field.valueType === TechSheetFieldType.SINGLE_SELECT
                    ? [values]
                    : values) as string[]
                )
                  .filter(Boolean)
                  .map((value) => {
                    return field.allowedValues?.find((v) => v?.internalName === value)?.value;
                  });

          return (
            <Chip
              defaultChecked
              key={field.internalName}
              color={TagColors.Neutral}
              title={`${field.label}: ${renderedValues.join(", ")}`}
              labelClassName="flex items-center gap-1"
              onChange={() => {
                if (field.internalName) {
                  setAppliedFilters((filters) => ({
                    ...filters,
                    techSheetFieldsSlugs: {
                      ...filters.techSheetFieldsSlugs,
                      [slugify(field.internalName!)]: EMPTY_OPTION_VALUE,
                    },
                  }));
                }
              }}
            >
              <span className="font-bold">{field.label}</span>:
              <span className="truncate max-w-[8ch]">{renderedValues[0]}</span>
              {renderedValues.length > 1 && <span>{`(+${renderedValues.length - 1})`}</span>}
            </Chip>
          );
        })}
      </div>
      <button
        className="font-bold text-primary hover:underline p-2 flex-none"
        type="submit"
        onClick={() => {
          setAppliedFilters(getDefaultProductFinderFilters(specializations));
        }}
      >
        {labels["product-finder-panel-clear-filters"]}
      </button>
    </div>
  );
};
